<template> 
    <div>
        <!-- Header Slider -->
        <div v-if="slideImages && slideImages.length" class="mb-4 mb-lg-10">
            <v-carousel :show-arrows-on-hover="phoneOnly" cycle :height="pcOnly ? 400 : 170" interval="10000" :show-arrows="slideImages.length > 1" class="header-carousel" hide-delimiters :class="{'bordered': (!slideImages || !slideImages.length)}">
                <template v-slot:prev="{ on, attrs }">
                    <v-btn color="primary" v-bind="attrs" v-on="on" class="transparent" depressed :icon="phoneOnly" :small="phoneOnly">
                        <v-icon :large="!phoneOnly">mdi-chevron-left</v-icon>
                    </v-btn>
                </template>
                <template v-slot:next="{ on, attrs }">
                    <v-btn color="primary" v-bind="attrs" v-on="on" class="transparent" depressed :icon="phoneOnly" :small="phoneOnly">
                        <v-icon :large="!phoneOnly">mdi-chevron-right</v-icon>
                    </v-btn>
                </template>
                <v-carousel-item v-for="(img,i) in slideImages" :key="i" class="slide-image">
                    <a v-if="img.href" :href="img.href" :target="capRunning ? '_system' : '_blank'" class="slide-image-link">
                        <div class="slide-image-frame">
                            <div class="slide-image-frame__bg" :style="{ backgroundImage: `url(${img.url})` }" />
                            <div class="slide-image-frame__fg" :style="{ backgroundImage: `url(${img.url})` }" />
                        </div>
                    </a>
                    <div v-else class="slide-image-link">
                        <div class="slide-image-frame">
                            <div class="slide-image-frame__bg" :style="{ backgroundImage: `url(${img.url})` }" />
                            <div class="slide-image-frame__fg" :style="{ backgroundImage: `url(${img.url})` }" />
                        </div>
                    </div>
                </v-carousel-item>
            </v-carousel>
        </div>

        <v-card v-if="capRunning" class="pa-0 mb-4">
            <v-row dense class="student-quick-nav__grid ma-0">
                <v-col
                    v-for="item in quickNavItems"
                    :key="item.name"
                    :cols="item.larger ? 8 : 4"
                    md="3"
                    class="pa-1 pa-sm-2"
                >
                    <v-card
                        flat
                        :to="item.to"
                        style="background-color: #f5f5f5; border-color: rgba(0, 0, 0, 0.1)!important;"
                        :class="[
                            'student-quick-nav__tile bordered rounded text-center',
                            { 'student-quick-nav__tile--active primary--text': $route.name === item.name },
                        ]"
                        v-ripple="false"
                    >
                        <div class="student-quick-nav__icon-wrap d-flex align-center justify-center mx-auto mb-1">
                            <v-badge
                                v-if="item.badge"
                                :value="item.badgeValue > 0"
                                :content="item.badgeValue > 99 ? '99+' : String(item.badgeValue)"
                                overlap
                                bordered
                                color="primary"
                                offset-x="10"
                                offset-y="10"
                            >
                                <v-icon :color="item.color" size="30">{{ item.icon }}</v-icon>
                            </v-badge>
                            <v-icon v-else :color="item.color" size="30">{{ item.icon }}</v-icon>
                        </div>
                        <div class="student-quick-nav__label text--text">{{ item.label }}</div>
                    </v-card>
                </v-col>
            </v-row>
        </v-card>

        <!-- Home Content -->

        <v-alert v-if="homeData.low_balance_alert && !walletDisabled" dense small text type="error" color="error" class="px-3 e-link" @click="$router.push({name: 'wallet'})" v-ripple icon="mdi-wallet">
            <div class="details-text font-weight-medium">
                <b>No Enough balance:</b>
                <br>
                <span class="text--text">Recharge by visiting Wallet page.</span>
            </div>
        </v-alert>
        <v-alert v-if="suspendedSubscriptionAlerts.length && !walletDisabled" dense small text type="warning" color="orange darken-3" class="px-3 e-link mb-2" @click="$router.push({name: 'wallet'})" v-ripple icon="mdi-pause-circle">
            <div class="details-text font-weight-medium">
                <b>Subscription paused — insufficient balance</b>
                <div v-for="row in suspendedSubscriptionAlerts" :key="row.program_id" class="text--text mt-1">
                    <span class="text--text">{{ row.program_name }}</span>
                    <span v-if="row.shortfall > 0"> — add at least <span class="font-weight-semibold">{{ moneyFormat(row.shortfall, true) }}</span> to your wallet.</span>
                    <span v-else> — your wallet may cover the subscription; renewal will run automatically.</span>
                </div>
            </div>
        </v-alert>
        <v-row>
            <v-col v-if="!nextClass && !continueTarget && !walletDisabled && (!otherNextClasses || otherNextClasses.length === 0)"  cols="12">
                <v-alert color="grey" text :class="{'mb-0': phoneOnly}" outlined>
                    <div class="text--text"><strong>Congratulations! 🎉 Your account is ready.</strong><br> You can now <router-link :to="{name: 'courses'}">enroll in a course</router-link> to start your free trial classes.</div>
                </v-alert>
            </v-col>

            <v-col v-if="nextClass || continueTarget" cols="12" lg="6">

                <v-card
                    v-if="continueTarget"
                    :to="continueTarget"
                    class="continue-target-card bordered rounded mb-5 overflow-hidden"
                    v-ripple="false"
                >
                    <div class="d-flex align-center pa-3">
                        <div class="continue-target-card__media me-4 flex-shrink-0">
                            <v-img
                                v-if="continueItem && continueItem.image_url"
                                :src="continueItem.image_url"
                                width="100"
                                min-width="100"
                                height="72"
                                class="rounded"
                            />
                            <div v-else class="continue-target-card__placeholder d-flex align-center justify-center rounded">
                                <v-progress-circular v-if="continueItemLoading" indeterminate size="22" width="2" color="primary" />
                                <v-icon v-else color="primary" size="28">{{ continueTargetIcon }}</v-icon>
                            </div>
                        </div>
                        <div class="flex-grow-1 min-width-0">
                            <div class="details-text primary--text font-weight-medium mb-1">{{ continueTargetLabel }}</div>
                            <div class="font-weight-bold text--text text-truncate mb-1">{{ continueTargetTitle }}</div>
                            <div class="details-text muted--text">{{ continueTargetBody }}</div>
                        </div>
                        <div class="d-flex align-center ms-2 flex-shrink-0">
                            <span class="primary--text font-weight-semibold details-text me-1 d-none d-sm-inline">{{ continueTargetCta }}</span>
                            <v-icon color="primary">mdi-chevron-right</v-icon>
                        </div>
                    </div>
                </v-card>

                <v-card v-if="rescheduleRequests && rescheduleRequests.length" class="bordered px-2 py-1 mb-5 rounded">
                    <v-card-title>
                        <v-icon left :color="rescheduleRequests.length ? 'indigo darken-2' : ''">mdi-clock-edit</v-icon>
                        Reschedule Requests
                        <!-- <span v-if="rescheduleRequests.length" class="indigo--text text--darken-2 ms-2">({{rescheduleRequests.length}})</span> -->
                    </v-card-title>

                    <v-divider v-if="phoneOnly"></v-divider>
                    <!-- <v-card-subtitle class="mb-2">Open calendar to view more classes</v-card-subtitle> -->
                    <v-card-text :class="{'px-0 py-0': phoneOnly && rescheduleRequests.length}">
                        <v-alert v-if="!rescheduleRequests.length" text color="muted" type="info" icon="mdi-information-outline" :dense="phoneOnly">
                            <div :class="{'details-text': phoneOnly}">
                                No requests found.
                            </div>
                        </v-alert>
                        <div v-else>
                            <reschedule-requests :requests="rescheduleRequests" />
                        </div>
                    </v-card-text>
                </v-card>

                <v-card v-if="nextClass" class="bordered rounded px-lg-2 py-lg-1 mb-3">
                    <v-card-title class="pb-1">Next Class</v-card-title>
                    <class-card :class-data="nextClass" :reloading="loading" no-dialog />
                </v-card>
            </v-col>
            <v-col v-if="otherNextClasses && otherNextClasses.length" cols="12" lg="6">
                <v-card>
                    <v-list class="py-0">
                        <class-list-item v-for="(classData) in otherNextClasses" :key="classData.id" :class-data="classData" class="mb-3" />
                        <v-list-item dense class="bordered rounded overflow-hidden mb-2" :to="{name: 'calendar'}" v-ripple="false">
                            <v-list-item-content>
                                <v-list-item-title class="text--text">
                                    <v-icon size="20" class="me-2">mdi-calendar</v-icon>
                                    <span>My Calendar</span>
                                </v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-icon>
                                <v-icon>mdi-chevron-right</v-icon>
                            </v-list-item-icon>
                        </v-list-item>
                        <v-list-item v-if="!walletDisabled" dense class="bordered rounded overflow-hidden" :to="{name: 'wallet'}" v-ripple="false">
                            <v-list-item-content>
                                <v-list-item-title class="text--text">
                                    <v-icon size="20" color="primary" class="me-2">mdi-wallet</v-icon>
                                    <span>Wallet ( <span class="font-weight-semibold primary--text">{{ moneyFormat(homeData.balance, true) }}</span> )</span>
                                </v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-icon>
                                <v-icon>mdi-chevron-right</v-icon>
                            </v-list-item-icon>
                        </v-list-item>
                    </v-list>
                </v-card>
            </v-col>

        </v-row>


        <v-row v-if="featuredCourses.length" class="mb-2">
            <v-col cols="12">
                <v-card class="bordered rounded px-lg-2 py-lg-1 mb-3">
                    <v-card-title class="pb-4 d-flex justify-space-between align-center">
                        <div>Featured courses</div>
                        <div v-if="pcOnly">
                            <v-btn color="primary" depressed :to="{name: 'courses'}"> View all courses </v-btn>
                        </div>
                    </v-card-title>
                    <v-card-text class="pt-0">
                        <v-row>
                            <v-col v-for="course in featuredCourses" :key="course.id" cols="12" md="6" lg="4" xl="3">
                                <course-card :course="course" style="height: 100%" />
                            </v-col>
                        </v-row>
                        <div v-if="phoneOnly" class="text-center mt-4">
                            <v-btn color="primary" depressed block :to="{name: 'courses'}"> View all courses </v-btn>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import CourseCard from '@/components/custom/course-card'
import RescheduleRequests from './reschedule-requests'

import GlobalComputed from '@/helpers/global-computed'
import GlobalMethods from '@/helpers/global-methods'
import { resolveTargetRoute } from '@/helpers/redirect-query'
import ClassCard from '@/components/custom/class-card'
import ClassListItem from '@/components/custom/class-list-item'

export default {
    props: {
        loading: {type: Boolean, default: false}
    },
    data(){
        return {
            interval: null,
            continueItem: null,
            continueItemLoading: false,
        }
    },
    components: {
        CourseCard,
        RescheduleRequests,
        ClassCard,
        ClassListItem,
    },
    computed: {
        ...GlobalComputed,
        homeData(){
            return this.$store.state.userModule.homeData;
        },
        nextClass(){
            return this.homeData ? this.homeData.next_class : null;
        },
        otherNextClasses(){
            return this.homeData ? this.homeData.other_next_classes : null;
        },
        rescheduleRequests(){
            return this.homeData ? this.homeData.reschedule_requests : null;
        },
        slideImages(){
            return this.homeData ? this.homeData.slide_images : [];
        },
        suspendedSubscriptionAlerts(){
            const a = this.homeData && this.homeData.suspended_subscriptions_alert;
            return Array.isArray(a) ? a : [];
        },
        featuredCourses(){
            const c = this.homeData && this.homeData.featured_courses;
            return Array.isArray(c) ? c : [];
        },
        walletDisabled(){
            return !this.appData.enable_app_w;
        },
        continueTarget(){
            return resolveTargetRoute(this.$route.query);
        },
        continueIsProgram(){
            return this.continueTarget && this.continueTarget.name === 'program';
        },
        continueTargetIcon(){
            return this.continueIsProgram ? 'mdi-account-group' : 'mdi-school';
        },
        continueTargetLabel(){
            return this.continueIsProgram ? 'Join now:' : 'Enroll now in:';
        },
        continueTargetTitle(){
            if (this.continueItem && this.continueItem.name){
                return this.continueItem.name;
            }
            return this.continueIsProgram ? '____' : '____';
        },
        continueTargetBody(){
            return this.continueIsProgram
                ? 'Click to join the program and start your classes.'
                : 'Click to enroll and schedule your free trial class.';
        },
        continueTargetCta(){
            return this.continueIsProgram ? 'View program' : 'View course';
        },
        quickNavItems(){
            const n = this.unreadInboxCount || 0;
            const items = [
                { name: 'courses', to: { name: 'courses' }, label: 'Courses', icon: 'mdi-school-outline', color: 'primary' },
                { name: 'programs', to: { name: 'programs' }, label: 'Group classes', icon: 'mdi-account-group-outline', color: 'primary' },
                { name: 'my-courses', to: { name: 'my-courses' }, label: 'My courses', icon: 'mdi-view-day-outline', color: 'orange darken-3' },
                { name: 'calendar', to: { name: 'calendar' }, label: 'Calendar', icon: 'mdi-calendar-month-outline', color: 'text', larger: this.walletDisabled },
                { name: 'class-reports', to: { name: 'class-reports' }, label: 'Class reports', icon: 'mdi-file-account-outline', color: 'orange darken-3' },
                { name: 'wallet', to: { name: 'wallet' }, label: 'Wallet', icon: 'mdi-wallet-outline', color: 'primary' },
                // { name: 'inbox', to: { name: 'inbox' }, label: 'Inbox', icon: 'mdi-inbox-outline', color: 'blue darken-1', badge: true, badgeValue: n },
                // { name: 'help', to: { name: 'help' }, label: 'Help', icon: 'mdi-lifebuoy', color: 'teal darken-2' },
                // { name: 'settings', to: { name: 'settings' }, label: 'Settings', icon: 'mdi-cog-outline', color: 'text' },
            ];
            if (this.walletDisabled){
                return items.filter((i) => i.name !== 'wallet');
            }
            return items;
        },
    },
    methods: {
        loadContinueItem(target){
            this.continueItem = null;
            if (!target){
                this.continueItemLoading = false;
                return;
            }
            const id = target.params.id;
            const type = target.name;
            const action = type === 'program' ? 'getProgram' : 'getCourse';
            this.continueItemLoading = true;
            this.$store.dispatch(action, {id}).then(response=>{
                if (!this.continueTarget || this.continueTarget.name !== type || this.continueTarget.params.id !== id){
                    return;
                }
                this.continueItem = response.data.data;
            }).catch(()=>{
                // keep fallback title / icon
            }).finally(()=>{
                if (this.continueTarget && this.continueTarget.name === type && this.continueTarget.params.id === id){
                    this.continueItemLoading = false;
                }
            });
        },
        ...GlobalMethods
    },
    watch: {
        continueTarget: {
            immediate: true,
            handler(target){
                this.loadContinueItem(target);
            }
        }
    },
    created(){
        const timeout = this.appData.home_reload_timeout || 10000;
        this.interval = window.setInterval(()=>{
            this.$emit('load');
        }, timeout)
    },
    beforeDestroy(){
        window.clearInterval(this.interval);
    }
}
</script>

<style lang="scss" scoped>
    .continue-target-card{
        text-decoration: none;
        transition: border-color 0.15s ease;

        &__media{
            width: 100px;
            height: 70px;
        }

        &__placeholder{
            width: 100px;
            height: 70px;
            background-color: rgba(#8a1e25, 0.08);
        }

        &:hover{
            border-color: rgba(#8a1e25, 0.45) !important;
        }
    }

    .min-width-0{
        min-width: 0;
    }

    .header-carousel{
        border-radius: 10px!important;
        border-radius: 5px;
        border: 1px solid rgba(#000, 0.1);
    }

    .slide-image{
        transition: 0.1s ease all;
        &:active{
            opacity: 0.9;
        }
    }

    .slide-image-link{
        display: block;
        width: 100%;
        height: 100%;
    }

    .slide-image-frame{
        position: relative;
        width: 100%;
        height: 100%;
        overflow: hidden;

        &__bg,
        &__fg{
            position: absolute;
            inset: 0;
            background-position: center;
            background-repeat: no-repeat;
        }

        &__bg{
            background-size: cover;
            filter: blur(20px);
            transform: scale(1.08);
        }

        &__fg{
            background-size: contain;
            z-index: 1;
        }
    }

    .student-quick-nav__tile{
        transition: transform 0.18s ease, box-shadow 0.18s ease;
        min-height: 75px;
        padding: 6px 5px 12px;
        user-select: none;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;

        @media (min-width: 600px){
            min-height: 96px;
        }

        &:hover{
            transform: translateY(-2px);
        }
        &:active{
            transform: scale(0.97);
        }
    }

    .student-quick-nav__tile--active{
        box-shadow: inset 0 0 0 2px currentColor;
        background-color: rgba(0, 0, 0, 0.04);
    }
    .theme--dark .student-quick-nav__tile--active{
        background-color: rgba(255, 255, 255, 0.06);
    }

    .student-quick-nav__label{
        font-size: 11px;
        font-weight: 500;
        line-height: 1.25;
        max-width: 100%;
        display: -webkit-box;
        line-clamp: 2;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        // overflow: hidden;
        word-break: break-word;

        @media (min-width: 600px){
            font-size: 12px;
        }
    }

    .student-quick-nav__icon-wrap{
        min-height: 36px;
    }
</style>
<style lang="scss">
.v-window__prev, .v-window__next{
    background: none!important;
}
.v-carousel__controls{
    background: none!important;
    .v-carousel__controls__item{
        margin: 0 2px!important;

        .v-icon.mdi-circle{
            font-size: 15px!important;
        }
    }
}
</style>
